import { FC, ReactElement, useState, useRef, useEffect, useCallback } from 'react';

import helpIcon from '../../assets/icons/helpToolTip/helptoolTip.svg';
import { CLICK_OUTSIDE_EVENT } from '../constants/event.constants';
import toolTipConstants from '../constants/toolTipConstants';
import { getCountries } from '../services/countryService';

import Tooltip from './Tooltip';


export interface Country {
    code: string;
    name: string;
}

interface CountrySelectProps {
    value: string;
    onChange: (country: Country) => void;
}

const CountrySelect: FC<CountrySelectProps> = ({ value, onChange }): ReactElement => {
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [countries, setCountries] = useState<Country[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [filteredCountries, setFilteredCountries] = useState<Country[]>([]);
    const [activeFilter, setActiveFilter] = useState<string>(''); // Keeps the filter active
    const dropdownRef = useRef<HTMLDivElement>(null);
    const resetTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const selectedCountry = countries.find(c => c.code === value) || countries[0];

    // Update filtered countries when activeFilter or countries change
    useEffect(() => {
        if (activeFilter) {
            const filtered = countries.filter(country =>
                country.name.toLowerCase().startsWith(activeFilter.toLowerCase())
            );
            setFilteredCountries(filtered.length > 0 ? filtered : countries);
        } else {
            setFilteredCountries(countries);
        }
    }, [activeFilter, countries]);

    // Handle keyboard input for type-ahead search
    const handleKeyDown = useCallback((e: globalThis.KeyboardEvent) => {
        if (!isOpen) return;

        // Only handle letter keys for search
        if (e.key.length === 1 && /[a-zA-Z]/.test(e.key)) {
            e.preventDefault();

            // Clear previous timeout
            if (resetTimeoutRef.current) {
                clearTimeout(resetTimeoutRef.current);
            }

            // Add to search term and update active filter
            const newSearchTerm = searchTerm + e.key;
            setSearchTerm(newSearchTerm);
            setActiveFilter(newSearchTerm);

            // Set cooldown to reset only the search term (not the filter)
            resetTimeoutRef.current = setTimeout(() => {
                setSearchTerm('');
            }, 1500);
        }

        // Handle backspace to clear search and filter
        if (e.key === 'Backspace') {
            e.preventDefault();
            setSearchTerm('');
            setActiveFilter('');
            if (resetTimeoutRef.current) {
                clearTimeout(resetTimeoutRef.current);
            }
        }

        // Handle escape to close
        if (e.key === 'Escape') {
            setIsOpen(false);
            setSearchTerm('');
            setActiveFilter('');
        }
    }, [isOpen, searchTerm]);

    // Add keyboard listener when dropdown is open
    useEffect(() => {
        if (isOpen) {
            document.addEventListener('keydown', handleKeyDown);
        }
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [isOpen, handleKeyDown]);

    // Cleanup timeout on unmount
    useEffect(() => {
        return () => {
            if (resetTimeoutRef.current) {
                clearTimeout(resetTimeoutRef.current);
            }
        };
    }, []);

    useEffect(() => {
        const loadCountries = async () => {
            try {
                setLoading(true);
                const countryList = (await getCountries()) as Country[];
                setCountries(countryList);
                setFilteredCountries(countryList);
            } catch (_error) {
                setCountries([]);
                setFilteredCountries([]);
            } finally {
                setLoading(false);
            }
        };

        loadCountries();
    }, []);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };
        document.addEventListener(CLICK_OUTSIDE_EVENT, handleClickOutside);
        return () => document.removeEventListener(CLICK_OUTSIDE_EVENT, handleClickOutside);
    }, []);

    const handleSelect = (country: Country) => {
        onChange(country);
        setIsOpen(false);
        setSearchTerm('');
        setActiveFilter('');
    };

    const handleToggle = () => {
        setIsOpen(!isOpen);
        if (isOpen) {
            setSearchTerm('');
            setActiveFilter('');
        }
    };

    if (loading) {
        return (
            <div className="flex flex-col mb-[10px] bg-white">
                <div className="flex items-center justify-between mb-[10px]">
                    <label className="flex items-center font-noto font-medium text-[14px] text-[#344054] leading-[24px]">
                        <span className="inline-block w-[211px] h-[24px] leading-[24px]">Country or Region of Residence</span>
                    </label>
                </div>
                <div className="h-[44px] bg-gray-100 animate-pulse rounded-[6px]"></div>
            </div>
        );
    }

    if (countries.length === 0 || !selectedCountry) {
        return (
            <div className="flex flex-col mb-[10px] bg-white">
                <div className="flex items-center justify-between mb-[10px]">
                    <label className="flex items-center font-noto font-medium text-[14px] text-[#344054] leading-[24px]">
                        <span className="inline-block w-[211px] h-[24px] leading-[24px]">Country or Region of Residence</span>
                    </label>
                </div>
                <div className="h-[44px] bg-red-50 border border-red-200 rounded-[6px] flex items-center justify-center">
                    <span className="text-red-600 text-sm">Failed to load countries</span>
                </div>
            </div>
        );
    }

    return (
        <div className="flex flex-col mb-[10px] bg-white">
            <div className="flex items-center justify-between mb-[8px]">
                <label className="font-noto font-medium text-[14px] text-[#344054] leading-[24px]">
                    <span className="inline-block w-[211px] h-[24px] leading-[24px]">Country or Region of Residence</span>
                </label>
                <Tooltip
                    content={toolTipConstants.COUNTRY_REGISTRATION}
                >
                    <img
                        src={helpIcon}
                        alt="Help"
                        className="w-4 h-4 cursor-pointer"
                    />
                </Tooltip>
            </div>
            <div className="relative" ref={dropdownRef}>
                <button
                    type="button"
                    onClick={handleToggle}
                    className="flex items-center w-full h-[40px] py-[10px] px-[12px] gap-x-[8px] border border-[#667085] rounded-[6px] text-[14px] font-noto font-medium text-gray-900 bg-white focus:outline-none focus:border-[#030407] transition-colors"
                >
                    <div className="w-5 h-5 rounded-full overflow-hidden flex-shrink-0 border border-gray-200 relative bg-gray-100 flex items-center justify-center">
                        <span className={`fi fi-${selectedCountry.code.toLowerCase()} scale-150`} style={{ lineHeight: '1em' }} />
                    </div>
                    <span className="text-left h-[20px] leading-[20px] truncate">{selectedCountry.name}</span>
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor" className="w-[20px] h-[20px] ml-auto text-[#98A2B3] transition-transform">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
                    </svg>
                </button>

                {isOpen && (
                    <div className="absolute top-full left-0 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg z-20 max-h-60 overflow-y-auto">
                        {filteredCountries.map((country) => (
                            <button
                                key={country.code}
                                type="button"
                                className="flex items-center w-full px-4 py-2 text-sm text-left hover:bg-gray-50 transition-colors text-gray-700"
                                onClick={() => handleSelect(country)}
                            >
                                <div className="w-5 h-5 rounded-full overflow-hidden mr-3 border border-gray-200 flex-shrink-0 relative flex items-center justify-center bg-gray-100">
                                    <span className={`fi fi-${country.code.toLowerCase()} scale-150`} />
                                </div>
                                <span className="font-medium">{country.name}</span>
                            </button>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default CountrySelect;
